/**
 * API Client
 * 
 * Centralized client for making API calls to the game server
 */

import { API_URL, getAuthToken, getAuthHeaders } from '../config/api.js';

export class APIClient {
    constructor() {
        this.baseURL = API_URL;
    }
    
    /**
     * Make authenticated API request
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const headers = {
            ...getAuthHeaders(),
            ...options.headers
        };
        
        try {
            const response = await fetch(url, {
                ...options,
                headers
            });
            
            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Request failed' }));
                throw new Error(error.error || `HTTP ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error(`API request failed: ${endpoint}`, error);
            throw error;
        }
    }
    
    /**
     * Get player currencies
     */
    async getCurrencies() {
        const data = await this.request('/api/v1/user/currencies');
        return data.currencies;
    }
    
    /**
     * Update currencies
     */
    async updateCurrencies(currencyData) {
        return await this.request('/api/v1/user/currencies', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(currencyData)
        });
    }
    
    /**
     * Get currency usage
     */
    async getCurrencyUsage(featureType, periodType = 'daily') {
        const data = await this.request(`/api/v1/user/currency-usage?feature=${featureType}&period=${periodType}`);
        return data.used || 0;
    }
    
    /**
     * Record currency usage
     */
    async recordCurrencyUsage(usageData) {
        return await this.request('/api/v1/user/currency-usage', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(usageData)
        });
    }
    
    /**
     * Get user profile
     */
    async getProfile() {
        return await this.request('/api/v1/user/profile');
    }
    
    /**
     * Get user cities
     */
    async getCities() {
        const data = await this.request('/api/v1/user/cities');
        return data.cities || [];
    }
    
    /**
     * Get city data
     */
    async getCity(cityId) {
        return await this.request(`/api/v1/cities/${cityId}`);
    }
    
    /**
     * Update city
     */
    async updateCity(cityId, cityData) {
        return await this.request(`/api/v1/cities/${cityId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cityData)
        });
    }
    
    /**
     * Get world data
     */
    async getWorld(worldId) {
        return await this.request(`/api/v1/world/${worldId}`);
    }
    
    /**
     * Get world list
     */
    async getWorlds() {
        const data = await this.request('/api/v1/world');
        return data.worlds || [];
    }
    
    /**
     * Get world regions
     */
    async getWorldRegions(worldId, x, y, width, height) {
        const data = await this.request(
            `/api/v1/world/${worldId}/regions?x=${x}&y=${y}&width=${width}&height=${height}`
        );
        return data.regions || [];
    }
}

// Export singleton instance
export const apiClient = new APIClient();

